import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Box, F0Button, F0Dialog, F0Select, F0Text } from '@factorialco/f0-react'
import type { F0SelectItemObject } from '@factorialco/f0-react'
import { Input, NumberInput } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete } from '@factorialco/f0-react/icons/app'

import type {
  CostCenterForSelectV2Fragment,
  CostCenterSelectorV2Query,
  FinancialProjectForSelectV2Fragment,
  FinancialProjectSelectorV2Query,
  PoFieldDefinitionDetailFragment,
  PurchaseOrderDetailFieldsFragment,
  TaxRateForSelectV2Fragment,
  TaxRateSelectorV2Query,
} from 'generated/resources/graphql'
import {
  CostCenterSelectorV2Document,
  FinancialProjectSelectorV2Document,
  TaxRateSelectorV2Document,
} from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { fromCents, toCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { resolveFieldLabel } from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/utils'
import {
  isEditableColumn,
  isMonetaryFieldKey,
  isNumericFieldType,
  recomputeLineItemFields,
} from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/utils'

import type { LineItemDraft } from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/useEditPurchaseOrder'

/**
 * Maps line-item index → tax rate percentage (e.g. 21 for 21%).
 * The field value stores the entity ID, but we need the numeric rate
 * for live recomputation of tax_amount / total. Populated when the user
 * selects a tax rate via TaxRateSelectorV2's onChangeWithRate callback.
 */
type TaxRateCache = Record<number, number>

// ─── Helpers ────────────────────────────────────────────────────────────

/**
 * Convert a draft's monetary values from cents (backend) to display units.
 * Other values pass through unchanged.
 */
function centsToDisplayDraft(
  draft: LineItemDraft,
  allDefinitions: PoFieldDefinitionDetailFragment[]
): LineItemDraft {
  return {
    ...draft,
    fieldValues: draft.fieldValues.map((fv) => {
      const def = allDefinitions.find((d) => d.id === fv.fieldDefinitionId)
      if (def?.fieldKey && isMonetaryFieldKey(def.fieldKey) && fv.value) {
        const cents = parseInt(fv.value, 10)
        if (!isNaN(cents)) {
          return { ...fv, value: String(fromCents(cents)) }
        }
      }
      return fv
    }),
  }
}

/**
 * Convert a draft's editable monetary values from display to cents,
 * then recompute computed fields (which also produces cents).
 */
function displayToCentsDraft(
  draft: LineItemDraft,
  allDefinitions: PoFieldDefinitionDetailFragment[],
  taxRatePercentage?: number
): LineItemDraft {
  const convertedValues = draft.fieldValues.map((fv) => {
    const def = allDefinitions.find((d) => d.id === fv.fieldDefinitionId)
    if (def?.fieldKey && isMonetaryFieldKey(def.fieldKey) && !def.computed && fv.value) {
      const num = Number(fv.value)
      if (!isNaN(num)) {
        return { ...fv, value: String(toCents(num)) }
      }
    }
    return fv
  })

  const recomputed = recomputeLineItemFields(
    convertedValues,
    allDefinitions,
    undefined,
    taxRatePercentage
  )

  return { ...draft, fieldValues: recomputed }
}

/**
 * Determine whether a given field should use the recomputed value
 * instead of its original display value during local recomputation.
 *
 * - All computed fields always use recomputed values.
 * - discount_amount is "soft computed" — uses recomputed unless the user
 *   is currently editing discount_amount itself.
 * - discount_percentage uses recomputed when the user is editing
 *   discount_amount (reverse derivation).
 */
function shouldUseRecomputed(
  def: PoFieldDefinitionDetailFragment,
  editedFieldKey?: string
): boolean {
  if (def.computed) return true

  const key = def.fieldKey ?? ''

  // discount_amount is derived from percentage, unless the user just typed it
  if (key === 'discount_amount' && editedFieldKey !== 'discount_amount') return true
  // discount_percentage is derived from amount when user edits amount
  if (key === 'discount_percentage' && editedFieldKey === 'discount_amount') return true

  return false
}

/**
 * After a cell edit, recompute computed fields so they update live.
 *
 * 1. Convert editable monetary values to cents (for computation).
 * 2. Run recomputeLineItemFields to get computed values in cents.
 * 3. Convert computed monetary values back to display format.
 * 4. Keep editable values in their original display format, except
 *    "soft computed" fields that should update (bidirectional discount).
 */
function recomputeLocalDraft(
  draft: LineItemDraft,
  allDefinitions: PoFieldDefinitionDetailFragment[],
  editedFieldKey?: string,
  taxRatePercentage?: number
): LineItemDraft {
  // Convert editable monetary values to cents temporarily
  const centsValues = draft.fieldValues.map((fv) => {
    const def = allDefinitions.find((d) => d.id === fv.fieldDefinitionId)
    if (def?.fieldKey && isMonetaryFieldKey(def.fieldKey) && !def.computed && fv.value) {
      const num = Number(fv.value)
      if (!isNaN(num)) return { ...fv, value: String(toCents(num)) }
    }
    return fv
  })

  // Recompute computed fields (results in cents)
  const recomputed = recomputeLineItemFields(
    centsValues,
    allDefinitions,
    editedFieldKey,
    taxRatePercentage
  )

  // Merge: keep editable values in display format, convert recomputed monetary
  // values from cents to display format.
  return {
    ...draft,
    fieldValues: recomputed.map((fv) => {
      const def = allDefinitions.find((d) => d.id === fv.fieldDefinitionId)
      if (!def) return fv

      if (shouldUseRecomputed(def, editedFieldKey)) {
        // Recomputed monetary fields: cents → display
        if (isMonetaryFieldKey(def.fieldKey ?? '') && fv.value) {
          const cents = parseInt(fv.value, 10)
          if (!isNaN(cents)) return { ...fv, value: String(fromCents(cents)) }
        }
        // Recomputed non-monetary fields (e.g. discount_percentage): keep as-is
        return fv
      }

      // Editable fields: preserve original display value
      const original = draft.fieldValues.find((v) => v.fieldDefinitionId === fv.fieldDefinitionId)
      return original ?? fv
    }),
  }
}

/**
 * Format a display-format value for presentation in a read-only cell.
 * Monetary values are formatted with currency symbol, percentages get "%".
 */
function renderCellValue(
  value: string,
  def: PoFieldDefinitionDetailFragment,
  currency: string,
  locale: string
): string {
  if (!value) return ''

  const fieldKey = def.fieldKey ?? ''

  if (isMonetaryFieldKey(fieldKey)) {
    const num = Number(value)
    if (!isNaN(num)) {
      return MoneyWithCurrency({ amount: toCents(num), currency, locale })
    }
  }

  if (fieldKey === 'discount_percentage') {
    return `${value}%`
  }

  return value
}

/**
 * Hook that attaches native keydown/paste listeners to the first `<input>`
 * found inside the element the returned callback-ref is placed on.
 *
 * NumberInput uses `<input type="text" inputMode="decimal">` and filters via onChange.
 * When the field is empty, typing a letter causes `setDisplayStr("")` which is a no-op
 * (state unchanged) → React 18 bails out of re-render → the character stays in the DOM.
 * Native listeners on the real input prevent the character from ever being accepted.
 */
function useNumericKeyGuard(maxDecimals: number) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listenersRef = useRef<{
    keydown: (e: KeyboardEvent) => void
    paste: (e: ClipboardEvent) => void
  } | null>(null)

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const allowed = [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'Home',
        'End',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        '-',
      ]
      if (maxDecimals !== 0) {
        allowed.push('.', ',')
      }
      if (allowed.includes(e.key) || (e.key >= '0' && e.key <= '9')) return
      e.preventDefault()
    }

    const onPaste = (e: ClipboardEvent) => {
      const pasted = e.clipboardData?.getData('text') ?? ''
      if (maxDecimals === 0) {
        if (!/^-?\d+$/.test(pasted)) {
          e.preventDefault()
        }
      } else {
        const re = new RegExp(`^-?\\d*([.,]\\d{0,${maxDecimals}})?$`)
        if (!re.test(pasted) || pasted === '' || pasted === '-') {
          e.preventDefault()
        }
      }
    }

    listenersRef.current = { keydown: onKeyDown, paste: onPaste }

    const input = inputRef.current
    if (input) {
      input.addEventListener('keydown', onKeyDown)
      input.addEventListener('paste', onPaste)
    }

    return () => {
      if (input) {
        input.removeEventListener('keydown', onKeyDown)
        input.removeEventListener('paste', onPaste)
      }
    }
  }, [maxDecimals])

  /** Place this callback-ref on the F0Box that wraps NumberInput. */
  const callbackRef = useCallback((node: HTMLElement | SVGElement | null) => {
    if (inputRef.current && listenersRef.current) {
      inputRef.current.removeEventListener('keydown', listenersRef.current.keydown)
      inputRef.current.removeEventListener('paste', listenersRef.current.paste)
    }
    const input = node?.querySelector('input') ?? null
    inputRef.current = input
    if (input && listenersRef.current) {
      input.addEventListener('keydown', listenersRef.current.keydown)
      input.addEventListener('paste', listenersRef.current.paste)
    }
  }, [])

  return callbackRef
}

/**
 * Return the NumberInput configuration for a numeric/monetary/percentage field.
 */
function numericFieldConfig(def: PoFieldDefinitionDetailFragment, currency: string) {
  const fieldKey = def.fieldKey ?? ''

  if (fieldKey === 'quantity') {
    return { maxDecimals: 0, units: undefined }
  }

  if (isMonetaryFieldKey(fieldKey)) {
    return { maxDecimals: 2, units: currency }
  }

  if (fieldKey === 'discount_percentage') {
    return { maxDecimals: 2, units: '%' }
  }

  // Generic numeric
  return { maxDecimals: 2, units: undefined }
}

/**
 * Check whether a field should render a NumberInput.
 */
function isNumberInputField(def: PoFieldDefinitionDetailFragment): boolean {
  const fieldKey = def.fieldKey ?? ''
  if (isMonetaryFieldKey(fieldKey)) return true
  if (fieldKey === 'quantity') return true
  if (fieldKey === 'discount_percentage') return true
  if (isNumericFieldType(def.fieldType) && !isEntityFieldType(def.fieldType)) return true
  return false
}

/**
 * Check whether a field type represents an entity reference (rendered as a selector).
 */
function isEntityFieldType(fieldType: string): boolean {
  return fieldType === 'cost_center' || fieldType === 'project' || fieldType === 'tax_rate'
}

// ─── F0Select mapOptions helpers ────────────────────────────────────────

function mapCostCenterOption(
  item: CostCenterForSelectV2Fragment
): F0SelectItemObject<string, CostCenterForSelectV2Fragment> {
  return {
    value: item.id,
    label: item.code ? `${item.name} (${item.code})` : item.name,
    item,
  }
}

function mapProjectOption(
  item: FinancialProjectForSelectV2Fragment
): F0SelectItemObject<string, FinancialProjectForSelectV2Fragment> {
  return {
    value: item.id,
    label: item.code ? `${item.name} (${item.code})` : item.name,
    item,
  }
}

function mapTaxRateOption(
  item: TaxRateForSelectV2Fragment
): F0SelectItemObject<string, TaxRateForSelectV2Fragment> {
  const pct = (item.rate * 100).toFixed(2)
  const country = item.taxType?.countryCode
  return {
    value: item.id,
    label: country ? `${pct}% (${country})` : `${pct}%`,
    item,
  }
}

// ─── Guarded NumberInput cell (uses hook → must be a component) ─────────

interface GuardedNumberCellProps {
  label: string
  locale: string
  value: number | null
  onChange: (v: number | null) => void
  maxDecimals: number
  units?: string
  invalid: boolean
}

function GuardedNumberCell({
  label,
  locale,
  value,
  onChange,
  maxDecimals,
  units,
  invalid,
}: GuardedNumberCellProps) {
  const guardRef = useNumericKeyGuard(maxDecimals)

  return (
    <F0Box ref={guardRef} width="full">
      <NumberInput
        label={label}
        hideLabel
        size="sm"
        locale={locale}
        value={isNaN(value ?? NaN) ? null : value}
        onChange={onChange}
        maxDecimals={maxDecimals}
        units={units}
        status={invalid ? { type: 'error' } : undefined}
      />
    </F0Box>
  )
}

// ─── Props ──────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean
  onClose: () => void
  lineItems: LineItemDraft[]
  columnDefinitions: PoFieldDefinitionDetailFragment[]
  allDefinitions: PoFieldDefinitionDetailFragment[]
  replaceLineItems: (items: LineItemDraft[]) => void
  purchaseOrder: PurchaseOrderDetailFieldsFragment
  locale: string
  /** Override currency for display; defaults to purchaseOrder.cost.currency */
  currency?: string
}

// ─── Component ──────────────────────────────────────────────────────────

function EditLineItemsModal({
  isOpen,
  onClose,
  lineItems,
  columnDefinitions,
  allDefinitions,
  replaceLineItems,
  purchaseOrder,
  locale,
  currency: currencyProp,
}: Props) {
  const currency = currencyProp ?? purchaseOrder.cost.currency

  // ─── F0Select data sources (shared across all rows) ───────────────
  const costCenterSource = useGraphqlDataSource(
    {
      document: CostCenterSelectorV2Document,
      paginationType: 'infinite-scroll',
      perPage: 20,
      mapVariables: ({ search }) => ({ search: search ?? undefined }),
      mapData: (data: CostCenterSelectorV2Query) =>
        data.costCenters?.costCentersConnection?.edges?.map((e) => e.node) ?? [],
      mapPagination: (data: CostCenterSelectorV2Query) => ({
        total: data.costCenters?.costCentersConnection?.totalCount ?? 0,
        cursor: data.costCenters?.costCentersConnection?.pageInfo?.endCursor ?? undefined,
        hasNextPage: data.costCenters?.costCentersConnection?.pageInfo?.hasNextPage ?? false,
      }),
    },
    []
  )

  const projectSource = useGraphqlDataSource(
    {
      document: FinancialProjectSelectorV2Document,
      paginationType: 'infinite-scroll',
      perPage: 20,
      mapVariables: ({ search }) => ({ search: search ?? undefined }),
      mapData: (data: FinancialProjectSelectorV2Query) =>
        data.projectManagement?.financialProjectsConnection?.edges?.map((e) => e.node) ?? [],
      mapPagination: (data: FinancialProjectSelectorV2Query) => ({
        total: data.projectManagement?.financialProjectsConnection?.totalCount ?? 0,
        cursor:
          data.projectManagement?.financialProjectsConnection?.pageInfo?.endCursor ?? undefined,
        hasNextPage:
          data.projectManagement?.financialProjectsConnection?.pageInfo?.hasNextPage ?? false,
      }),
    },
    []
  )

  const taxRateSource = useGraphqlDataSource(
    {
      document: TaxRateSelectorV2Document,
      paginationType: 'infinite-scroll',
      perPage: 20,
      mapVariables: ({ search }) => ({ search: search ?? undefined }),
      mapData: (data: TaxRateSelectorV2Query) =>
        data.finance?.taxRatesConnection?.edges?.map((e) => e.node) ?? [],
      mapPagination: (data: TaxRateSelectorV2Query) => ({
        total: data.finance?.taxRatesConnection?.totalCount ?? 0,
        cursor: data.finance?.taxRatesConnection?.pageInfo?.endCursor ?? undefined,
        hasNextPage: data.finance?.taxRatesConnection?.pageInfo?.hasNextPage ?? false,
      }),
    },
    []
  )

  // ─── Local editing state (values in display format, not cents) ─────
  const [localDrafts, setLocalDrafts] = useState<LineItemDraft[]>([])
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false)

  // ─── Tax rate percentage cache for live recomputation ───────────
  // The field value stores the entity ID. This ref holds the numeric
  // rate (e.g. 21 for 21%) so computed fields update in real time.
  // Using a ref so it's always current inside setState callbacks.
  const taxRateCacheRef = useRef<TaxRateCache>({})

  // Lazy query to fetch tax rates by ID on modal open, so the cache
  // is pre-populated for existing line items that already have a tax_rate.
  const [fetchTaxRates] = useLazyQuery(TaxRateSelectorV2Document, {
    onCompleted(data: TaxRateSelectorV2Query) {
      const nodes = data.finance?.taxRatesConnection?.edges?.map((e) => e.node) ?? []
      const rateById: Record<string, number> = {}
      for (const node of nodes) {
        rateById[node.id] = Math.round(node.rate * 100 * 100) / 100
      }

      const taxRateDef = allDefinitions.find((d) => d.fieldKey === 'tax_rate')
      if (!taxRateDef) return

      const cache: TaxRateCache = {}
      localDrafts.forEach((draft, idx) => {
        const fv = draft.fieldValues.find((v) => v.fieldDefinitionId === taxRateDef.id)
        const rate = fv?.value ? rateById[fv.value] : undefined
        if (rate !== undefined) {
          cache[idx] = rate
        }
      })
      taxRateCacheRef.current = { ...taxRateCacheRef.current, ...cache }
    },
  })

  // Re-initialize when the modal opens — convert cents to display format
  // and pre-populate the tax rate cache for existing line items.
  useEffect(() => {
    if (isOpen) {
      const displayDrafts = lineItems.map((d) => centsToDisplayDraft(d, allDefinitions))
      setLocalDrafts(displayDrafts)
      setHasAttemptedSave(false)
      taxRateCacheRef.current = {}

      // Collect unique tax_rate entity IDs from existing line items
      const taxRateDef = allDefinitions.find((d) => d.fieldKey === 'tax_rate')
      if (taxRateDef) {
        const taxRateIds = lineItems
          .map((li) => li.fieldValues.find((fv) => fv.fieldDefinitionId === taxRateDef.id)?.value)
          .filter((v): v is string => Boolean(v))
        const uniqueIds = [...new Set(taxRateIds)]

        if (uniqueIds.length > 0) {
          fetchTaxRates({ variables: { ids: uniqueIds, perPage: uniqueIds.length } })
        }
      }
    }
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Cell change handler (text and numeric) ───────────────────────
  const handleCellChange = useCallback(
    (
      draftIndex: number,
      definitionId: string,
      rawValue: string,
      fieldKey?: string,
      displayVal?: string
    ) => {
      setLocalDrafts((prev) => {
        const newDrafts = [...prev]
        const draft = newDrafts[draftIndex]
        if (!draft) return prev

        const newFieldValues = [...draft.fieldValues]
        const fvIndex = newFieldValues.findIndex((fv) => fv.fieldDefinitionId === definitionId)
        const entry = { fieldDefinitionId: definitionId, value: rawValue, displayValue: displayVal }
        if (fvIndex >= 0) {
          newFieldValues[fvIndex] = entry
        } else {
          newFieldValues.push(entry)
        }

        const updatedDraft = { ...draft, fieldValues: newFieldValues }
        const ratePercentage = taxRateCacheRef.current[draftIndex]
        newDrafts[draftIndex] = recomputeLocalDraft(
          updatedDraft,
          allDefinitions,
          fieldKey,
          ratePercentage
        )
        return newDrafts
      })
    },
    [allDefinitions]
  )

  // ─── NumberInput change handler (bridges number|null to string) ────
  const handleNumberChange = useCallback(
    (draftIndex: number, definitionId: string, numValue: number | null, fieldKey?: string) => {
      const strValue = numValue !== null ? String(numValue) : ''
      handleCellChange(draftIndex, definitionId, strValue, fieldKey)
    },
    [handleCellChange]
  )

  // ─── Tax rate selector change handler ──────────────────────────────
  // Stores the entity ID as the field value and caches the rate for recomputation.
  const handleTaxRateChange = useCallback(
    (
      draftIndex: number,
      definitionId: string,
      entityId: string | undefined,
      rateDecimal: number | undefined,
      displayVal?: string
    ) => {
      const ratePercentage =
        rateDecimal !== undefined ? Math.round(rateDecimal * 100 * 100) / 100 : 0
      taxRateCacheRef.current = { ...taxRateCacheRef.current, [draftIndex]: ratePercentage }
      handleCellChange(draftIndex, definitionId, entityId ?? '', 'tax_rate', displayVal)
    },
    [handleCellChange]
  )

  // ─── Add row (local) ──────────────────────────────────────────────
  const handleAddRow = useCallback(() => {
    setLocalDrafts((prev) => {
      const newPosition = prev.length + 1
      const fieldValues = columnDefinitions
        .filter((d) => !d.computed && d.fieldKey !== 'line_number')
        .map((d) => ({ fieldDefinitionId: d.id, value: '' }))

      return [...prev, { position: newPosition, fieldValues }]
    })
  }, [columnDefinitions])

  // ─── Remove row (local) ───────────────────────────────────────────
  const handleRemoveRow = useCallback((draftIndex: number) => {
    setLocalDrafts((prev) =>
      prev
        .filter((_, idx) => idx !== draftIndex)
        .map((item, idx) => ({ ...item, position: idx + 1 }))
    )
    // Re-index the tax rate cache after row removal
    const prevCache = taxRateCacheRef.current
    const nextCache: TaxRateCache = {}
    Object.entries(prevCache).forEach(([key, val]) => {
      const idx = Number(key)
      if (idx < draftIndex) nextCache[idx] = val
      if (idx > draftIndex) nextCache[idx - 1] = val
    })
    taxRateCacheRef.current = nextCache
  }, [])

  // ─── Validation: check all editable fields are filled ──────────────
  const hasValidationErrors = useMemo(() => {
    const editableDefs = columnDefinitions.filter(
      (d) => isEditableColumn(d) && d.fieldKey !== 'line_number'
    )
    return localDrafts.some((draft) =>
      editableDefs.some((def) => {
        const fv = draft.fieldValues.find((v) => v.fieldDefinitionId === def.id)
        return !fv?.value
      })
    )
  }, [localDrafts, columnDefinitions])

  const isCellInvalid = useCallback(
    (draft: LineItemDraft, def: PoFieldDefinitionDetailFragment): boolean => {
      if (!hasAttemptedSave) return false
      if (!isEditableColumn(def) || def.fieldKey === 'line_number') return false
      const fv = draft.fieldValues.find((v) => v.fieldDefinitionId === def.id)
      return !fv?.value
    },
    [hasAttemptedSave]
  )

  // ─── Done: validate, convert display→cents, recompute, sync to parent
  const handleDone = useCallback(() => {
    if (hasValidationErrors) {
      setHasAttemptedSave(true)
      return
    }

    const cache = taxRateCacheRef.current
    const finalDrafts = localDrafts.map((draft, idx) =>
      displayToCentsDraft(draft, allDefinitions, cache[idx])
    )
    replaceLineItems(finalDrafts)
    onClose()
  }, [localDrafts, allDefinitions, replaceLineItems, onClose, hasValidationErrors])

  // ─── Column labels for headers ────────────────────────────────────
  const columnHeaders = useMemo(
    () => columnDefinitions.map((def) => resolveFieldLabel(def)),
    [columnDefinitions]
  )

  // ─── Dynamic grid template ──────────────────────────────────────
  const gridStyle = {
    display: 'grid' as const,
    gridTemplateColumns: `repeat(${columnDefinitions.length}, minmax(120px, 1fr)) 48px`,
  }

  // ─── Render a single cell ─────────────────────────────────────────
  const renderCell = (
    draft: LineItemDraft,
    draftIndex: number,
    def: PoFieldDefinitionDetailFragment
  ) => {
    const cellLabel = resolveFieldLabel(def)

    // Line number — always display-only
    if (def.fieldKey === 'line_number') {
      return (
        <F0Box key={def.id} paddingX="sm" paddingY="sm" display="flex" alignItems="center">
          <F0Text content={`#${draft.position}`} variant="body" />
        </F0Box>
      )
    }

    const fv = draft.fieldValues.find((v) => v.fieldDefinitionId === def.id)
    const value = fv?.value ?? ''

    // Computed fields — display-only with formatted value
    if (!isEditableColumn(def)) {
      return (
        <F0Box key={def.id} paddingX="sm" paddingY="sm" display="flex" alignItems="center">
          <F0Text content={renderCellValue(value, def, currency, locale)} variant="body" />
        </F0Box>
      )
    }

    // ─── Entity selectors (F0Select direct, bypasses EntitySelectorV2 portal bug) ──
    if (def.fieldType === 'cost_center') {
      const invalid = isCellInvalid(draft, def)
      return (
        <F0Box
          key={def.id}
          paddingX="xs"
          paddingY="xs"
          display="flex"
          alignItems="center"
          width="full"
        >
          <Box style={{ width: '100%', maxWidth: '100%' }}>
            <F0Select<string, CostCenterForSelectV2Fragment>
              label={cellLabel}
              hideLabel
              size="sm"
              source={costCenterSource}
              mapOptions={mapCostCenterOption}
              value={value || undefined}
              onChange={(
                id: string,
                _originalItem: CostCenterForSelectV2Fragment | undefined,
                option: F0SelectItemObject<string, CostCenterForSelectV2Fragment> | undefined
              ) => handleCellChange(draftIndex, def.id, id ?? '', undefined, option?.label)}
              clearable
              showSearchBox
              status={invalid ? { type: 'error' } : undefined}
            />
          </Box>
        </F0Box>
      )
    }

    if (def.fieldType === 'project') {
      const invalid = isCellInvalid(draft, def)
      return (
        <F0Box
          key={def.id}
          paddingX="xs"
          paddingY="xs"
          display="flex"
          alignItems="center"
          width="full"
        >
          <Box style={{ width: '100%', maxWidth: '100%' }}>
            <F0Select<string, FinancialProjectForSelectV2Fragment>
              label={cellLabel}
              hideLabel
              size="sm"
              source={projectSource}
              mapOptions={mapProjectOption}
              value={value || undefined}
              onChange={(
                id: string,
                _originalItem: FinancialProjectForSelectV2Fragment | undefined,
                option: F0SelectItemObject<string, FinancialProjectForSelectV2Fragment> | undefined
              ) => handleCellChange(draftIndex, def.id, id ?? '', undefined, option?.label)}
              clearable
              showSearchBox
              status={invalid ? { type: 'error' } : undefined}
            />
          </Box>
        </F0Box>
      )
    }

    if (def.fieldKey === 'tax_rate') {
      const invalid = isCellInvalid(draft, def)
      return (
        <F0Box
          key={def.id}
          paddingX="xs"
          paddingY="xs"
          display="flex"
          alignItems="center"
          width="full"
        >
          <Box style={{ width: '100%', maxWidth: '100%' }}>
            <F0Select<string, TaxRateForSelectV2Fragment>
              label={cellLabel}
              hideLabel
              size="sm"
              source={taxRateSource}
              mapOptions={mapTaxRateOption}
              value={value || undefined}
              onChange={(
                id: string,
                originalItem: TaxRateForSelectV2Fragment | undefined,
                option: F0SelectItemObject<string, TaxRateForSelectV2Fragment> | undefined
              ) => {
                handleTaxRateChange(draftIndex, def.id, id, originalItem?.rate, option?.label)
              }}
              clearable
              showSearchBox
              status={invalid ? { type: 'error' } : undefined}
            />
          </Box>
        </F0Box>
      )
    }

    // ─── NumberInput for numeric/monetary/percentage fields ────────
    if (isNumberInputField(def)) {
      const config = numericFieldConfig(def, currency)
      const numValue = value !== '' ? Number(value) : null
      const invalid = isCellInvalid(draft, def)

      return (
        <F0Box
          key={def.id}
          paddingX="xs"
          paddingY="xs"
          display="flex"
          alignItems="center"
          width="full"
        >
          <GuardedNumberCell
            label={cellLabel}
            locale={locale}
            value={numValue}
            onChange={(v) => handleNumberChange(draftIndex, def.id, v, def.fieldKey ?? undefined)}
            maxDecimals={config.maxDecimals}
            units={config.units}
            invalid={invalid}
          />
        </F0Box>
      )
    }

    // ─── Text Input for text fields ───────────────────────────────
    const invalid = isCellInvalid(draft, def)
    return (
      <F0Box
        key={def.id}
        paddingX="xs"
        paddingY="xs"
        display="flex"
        alignItems="center"
        width="full"
        overflow="hidden"
      >
        <Input
          label={cellLabel}
          hideLabel
          size="sm"
          value={value}
          onChange={(newValue) =>
            handleCellChange(draftIndex, def.id, newValue, def.fieldKey ?? undefined)
          }
          status={invalid ? { type: 'error' } : undefined}
        />
      </F0Box>
    )
  }

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={i18n.t('procurement.purchase_orders.detail.line_items.edit_title')}
      primaryAction={{
        label: i18n.t('procurement.purchase_orders.detail.save'),
        onClick: handleDone,
      }}
      secondaryAction={{
        label: i18n.t('procurement.purchase_orders.detail.cancel'),
        onClick: onClose,
      }}
      container={document.body}
      position="center"
      width="xl"
    >
      <F0Box width="full" paddingX="xl" paddingY="md">
        <F0Box
          border="default"
          borderColor="secondary"
          borderRadius="sm"
          display="flex"
          flexDirection="column"
          divider="y"
          dividerColor="secondary"
          background="primary"
          overflowX="auto"
        >
          {/* Column headers */}
          <Box style={gridStyle}>
            {columnDefinitions.map((def, colIdx) => (
              <F0Box key={def.id} paddingX="sm" paddingY="sm">
                <F0Text content={columnHeaders[colIdx] ?? ''} variant="description" />
              </F0Box>
            ))}
            {/* Empty header for the delete column */}
            <F0Box paddingX="sm" paddingY="sm" />
          </Box>

          {/* Data rows */}
          {localDrafts.map((draft, draftIndex) => (
            <Box key={draft.id ?? `new-${draftIndex}`} style={gridStyle}>
              {columnDefinitions.map((def) => renderCell(draft, draftIndex, def))}
              <F0Box paddingX="xs" paddingY="xs" display="flex" alignItems="center">
                <F0Button
                  variant="critical"
                  icon={Delete}
                  label={i18n.t('procurement.purchase_orders.detail.line_items.delete')}
                  hideLabel
                  size="sm"
                  onClick={() => handleRemoveRow(draftIndex)}
                />
              </F0Box>
            </Box>
          ))}

          {/* Empty state */}
          {localDrafts.length === 0 && (
            <Box style={gridStyle}>
              {columnDefinitions.map((def) => (
                <F0Box key={def.id} paddingX="sm" paddingY="sm">
                  <F0Text content=" " variant="body" />
                </F0Box>
              ))}
              <F0Box paddingX="sm" paddingY="sm" />
            </Box>
          )}
        </F0Box>

        {/* Add line button */}
        <F0Box paddingY="sm">
          <F0Button
            variant="outline"
            icon={Add}
            label={i18n.t('procurement.purchase_orders.detail.line_items.new_row')}
            size="sm"
            onClick={handleAddRow}
            type="button"
          />
        </F0Box>
      </F0Box>
    </F0Dialog>
  )
}

export default EditLineItemsModal
